import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Users, Monitor, Plus, Edit } from 'lucide-react';
import { toast } from 'sonner';

export default function HouseholdDetails() {
  const { householdId } = useParams();
  const navigate = useNavigate();
  const [household, setHousehold] = useState(null);
  const [devices, setDevices] = useState([]);
  const [seniors, setSeniors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHouseholdData();
  }, [householdId]);

  const fetchHouseholdData = async () => {
    setLoading(true);
    try {
      const [householdRes, devicesRes, seniorsRes] = await Promise.all([
        axios.get(`${API}/households/${householdId}`),
        axios.get(`${API}/households/${householdId}/devices`),
        axios.get(`${API}/households/${householdId}/seniors`)
      ]);
      
      setHousehold(householdRes.data);
      setDevices(devicesRes.data);
      setSeniors(seniorsRes.data);
    } catch (error) {
      console.error('Error fetching household data:', error);
      toast.error('Fehler beim Laden der Haushaltsdaten');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Haushaltsdaten...</p>
        </div>
      </div>
    );
  }

  if (!household) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card>
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-semibold mb-2">Haushalt nicht gefunden</h3>
            <Button onClick={() => navigate('/households')}>
              Zurück zur Übersicht
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <Button
          variant="outline"
          onClick={() => navigate('/households')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück zur Übersicht
        </Button>
        
        <Card className="glass-effect">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{household.name}</h1>
                <p className="text-gray-600 mt-1">
                  Erstellt: {new Date(household.created_at).toLocaleDateString('de-DE')}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => navigate(`/households/${householdId}/activity`)}>
                  📊 Aktivitäten
                </Button>
                <Button variant="outline" onClick={() => navigate(`/households/${householdId}/family`)}>
                  👥 Familie
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        <Tabs defaultValue="seniors" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="seniors">
              <Users className="w-4 h-4 mr-2" />
              Senioren ({seniors.length})
            </TabsTrigger>
            <TabsTrigger value="devices">
              <Monitor className="w-4 h-4 mr-2" />
              Geräte ({devices.length})
            </TabsTrigger>
          </TabsList>

          {/* Seniors Tab */}
          <TabsContent value="seniors" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Senioren-Profile</h2>
              <Button onClick={() => navigate(`/households/${householdId}/seniors/new`)}>
                <Plus className="w-4 h-4 mr-2" />
                Senior hinzufügen
              </Button>
            </div>

            {seniors.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <Users className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Keine Senioren-Profile</h3>
                  <p className="text-gray-600 mb-4">Fügen Sie das erste Senior-Profil hinzu</p>
                  <Button onClick={() => navigate(`/households/${householdId}/seniors/new`)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Senior hinzufügen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {seniors.map((senior) => (
                  <Card key={senior.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <CardTitle>{senior.name}</CardTitle>
                      <CardDescription>
                        <Badge variant="outline">{senior.language.toUpperCase()}</Badge>
                        {senior.religion !== 'none' && (
                          <Badge variant="secondary" className="ml-2">{senior.religion}</Badge>
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Erinnerungen:</span>
                          <span className="font-semibold">
                            {senior.modules_enabled?.reminders ? '✓' : '✗'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Notfall:</span>
                          <span className="font-semibold">
                            {senior.modules_enabled?.emergency ? '✓' : '✗'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Religion:</span>
                          <span className="font-semibold">
                            {senior.modules_enabled?.religion_module ? '✓' : '✗'}
                          </span>
                        </div>
                      </div>
                      <Button 
                        className="w-full mt-4" 
                        variant="outline"
                        onClick={() => navigate(`/seniors/${senior.id}`)}
                      >
                        Details
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Devices Tab */}
          <TabsContent value="devices" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Geräte</h2>
              <Button onClick={() => navigate(`/households/${householdId}/devices/register`)}>
                <Plus className="w-4 h-4 mr-2" />
                Gerät registrieren
              </Button>
            </div>

            {devices.length === 0 ? (
              <Card>
                <CardContent className="pt-6 text-center">
                  <Monitor className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Keine Geräte</h3>
                  <p className="text-gray-600 mb-4">Registrieren Sie das erste Gerät</p>
                  <Button onClick={() => navigate(`/households/${householdId}/devices/register`)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Gerät registrieren
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {devices.map((device) => (
                  <Card key={device.id}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{device.serial_number}</span>
                        <Badge variant={device.status === 'online' ? 'default' : 'secondary'}>
                          {device.status}
                        </Badge>
                      </CardTitle>
                      <CardDescription>
                        Gerät ID: {device.id.substring(0, 8)}...
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm space-y-2 mb-4">
                        {device.pairing_code && (
                          <div>
                            <span className="text-gray-600">Pairing Code:</span>
                            <span className="ml-2 font-mono font-bold">{device.pairing_code}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-600">Erstellt:</span>
                          <span className="ml-2">{new Date(device.created_at).toLocaleDateString('de-DE')}</span>
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={() => navigate(`/device/${device.id}`)}
                        className="w-full"
                      >
                        <Monitor className="w-4 h-4 mr-2" />
                        Zur Senioren-Ansicht (Device UI)
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
